import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { jobsAPI, getErrorMessage } from '../services/api';
import QuestionsTab from '../components/QuestionsTab';
import CandidatesTab from '../components/CandidatesTab';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import type { Job, UpdateJobRequest } from '../types';

interface JobWithCounts extends Job {
  questions_count?: number;
  candidates_count?: number;
}

function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState<JobWithCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<UpdateJobRequest>({
    title: '',
    company: '',
    description: '',
  });

  // Get active tab from URL or default to overview
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'overview';

  const loadJob = useCallback(async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const data = await jobsAPI.getById(Number(jobId));
      setJob(data as JobWithCounts);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const handleTabChange = (tab: string) => {
    navigate(`/dashboard/jobs/${jobId}?tab=${tab}`);
  };

  const handleEdit = () => {
    if (!job) return;

    setFormData({
      title: job.title,
      company: job.company,
      description: job.description || '',
    });
    setShowEditModal(true);
  };

  const handleClose = async () => {
    if (!jobId || !window.confirm('Are you sure you want to close this job?')) {
      return;
    }

    try {
      await jobsAPI.close(Number(jobId));
      loadJob();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to close job');
    }
  };

  const handleDelete = async () => {
    if (!jobId || !window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    try {
      await jobsAPI.delete(Number(jobId));
      navigate('/dashboard/jobs');
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to delete job');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobId) return;

    try {
      await jobsAPI.update(Number(jobId), formData);
      setShowEditModal(false);
      loadJob();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to update job');
    }
  };

  if (loading) {
    return (
      <div>
        <div className="text-center py-10 text-gray-600">Loading job details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="bg-danger-light text-danger-text px-4 py-3 rounded-md mb-5">
          {error}
        </div>
        <Button variant="secondary" onClick={() => navigate('/dashboard/jobs')}>
          ← Back to Jobs
        </Button>
      </div>
    );
  }

  if (!job) {
    return (
      <div>
        <div className="bg-danger-light text-danger-text px-4 py-3 rounded-md mb-5">
          Job not found
        </div>
        <Button variant="secondary" onClick={() => navigate('/dashboard/jobs')}>
          ← Back to Jobs
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-5">
        <button
          className="text-gray-600 hover:text-dark transition-colors bg-transparent border-none p-0 cursor-pointer"
          onClick={() => navigate('/dashboard/jobs')}
        >
          ← Back to Jobs
        </button>
      </div>

      {/* Job Header Card */}
      <Card className="mb-5">
        <div className="flex justify-between items-start p-5">
          <div className="flex-1">
            <h1 className="m-0 mb-2 text-[28px] font-semibold">{job.title}</h1>
            <p className="m-0 mb-2 text-lg text-gray-600">{job.company}</p>
            {job.description && (
              <p className="m-0 text-gray-500 leading-relaxed">{job.description}</p>
            )}
            <div className="mt-4">
              <Badge variant={job.status === 'active' ? 'success' : 'warning'}>
                {job.status}
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleEdit}>
              Edit
            </Button>
            {job.status === 'active' && (
              <Button variant="warning" onClick={handleClose}>
                Close
              </Button>
            )}
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="mt-5">
        {/* Tabs Header */}
        <div className="flex gap-0 border-b-2 border-gray-300 mb-5">
          <button
            className={`px-6 py-3 bg-transparent border-none border-b-[3px] text-[15px] font-medium cursor-pointer transition-all duration-200 relative -mb-0.5 ${
              activeTab === 'overview'
                ? 'text-primary border-b-primary'
                : 'text-gray-600 border-b-transparent hover:text-dark hover:bg-primary-light'
            }`}
            onClick={() => handleTabChange('overview')}
          >
            Overview
          </button>
          <button
            className={`px-6 py-3 bg-transparent border-none border-b-[3px] text-[15px] font-medium cursor-pointer transition-all duration-200 relative -mb-0.5 ${
              activeTab === 'questions'
                ? 'text-primary border-b-primary'
                : 'text-gray-600 border-b-transparent hover:text-dark hover:bg-primary-light'
            }`}
            onClick={() => handleTabChange('questions')}
          >
            Questions
          </button>
          <button
            className={`px-6 py-3 bg-transparent border-none border-b-[3px] text-[15px] font-medium cursor-pointer transition-all duration-200 relative -mb-0.5 ${
              activeTab === 'candidates'
                ? 'text-primary border-b-primary'
                : 'text-gray-600 border-b-transparent hover:text-dark hover:bg-primary-light'
            }`}
            onClick={() => handleTabChange('candidates')}
          >
            Candidates
          </button>
        </div>

        {/* Tabs Content */}
        <div className="py-5">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-4 gap-5 mb-8">
              <Card className="p-5">
                <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Total Questions</h4>
                <p className="text-[32px] font-bold text-dark m-0">{job.questions_count || 0}</p>
              </Card>
              <Card className="p-5">
                <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Total Candidates</h4>
                <p className="text-[32px] font-bold text-dark m-0">{job.candidates_count || 0}</p>
              </Card>
              <Card className="p-5">
                <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Status</h4>
                <p className="text-[32px] font-bold text-dark m-0">{job.status}</p>
              </Card>
              <Card className="p-5">
                <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Created</h4>
                <p className="text-sm mt-2 m-0">
                  {new Date(job.created_at).toLocaleDateString()}
                </p>
              </Card>
            </div>
          )}

          {activeTab === 'questions' && jobId && (
            <QuestionsTab jobId={Number(jobId)} />
          )}

          {activeTab === 'candidates' && jobId && (
            <CandidatesTab jobId={Number(jobId)} />
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <Input
                  id="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g., Acme Inc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Job description, requirements, responsibilities..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default JobDetailPage;
